import { logger } from './logger.ts';
import { supabase } from '../lib/db.ts';
import { MarketEngine, MarketDataFromDb } from '../lib/marketEngine.ts';
import type {
  Market,
  MarketCategory,
  MarketStatus,
  DbMarketType,
  WalletTransaction,
  LMSRMarketState,
} from '../types.ts';
import { lmsrGrossSellProceeds, lmsrPrice } from '../lib/lmsrMath.ts';

const marketEngine = new MarketEngine();

type LocalPositionWithMarket = import('../types.ts').LocalPositionWithMarket;
type DetailedWalletTransaction = import('../types.ts').DetailedWalletTransaction;

interface FetchPortfolioDataOptions {
  betLimit?: number;
  includeWalletBalance?: boolean;
}

export interface PortfolioBet {
  id: string;
  user_id: string;
  market_id: string;
  market_option_id: number;
  amount: number;
  amount_after_fee: number;
  service_fee_amount: number;
  status: 'active' | 'won' | 'lost' | 'cancelled';
  time: string;
  initial_potential_win?: number | null;
  market: {
    id: string;
    title: string;
    category: string | null;
    resolution_time: string | null;
    trading_end_time: string | null;
    status: string;
    market_type: string | null;
    engine_type?: string | null;
    lmsr_b?: number | null;
  } | null;
  shares_owned?: number | null;
  lmsr_initial_stake_gross?: number | null;
  lmsr_initial_stake_net?: number | null;
  lmsr_exit_net?: number | null;
  /** LMSR: ZAR spread revenue attributed to this trade row (from place-bet / DB). */
  spread_revenue?: number | null;
  market_option: {
    id: number;
    name: string;
    trading_end_time: string | null;
  } | null;
}

export interface MarketPoolRow {
  market_id: string;
  market_option_id: number;
  total_amount: number;
}

export type RawCreatedMarket = MarketDataFromDb;

export interface RawPortfolioDataInput {
  bets: PortfolioBet[];
  marketPools: MarketPoolRow[];
  transactions: WalletTransaction[];
  createdMarkets: RawCreatedMarket[];
  walletBalance?: number | null;
  /** market_id → active options ordered by id (for LMSR pricing) */
  lmsrOptionsByMarket?: Map<string, { id: number; outstanding_shares: number }[]>;
  /** LMSR active positions: liquidation value `final_proceeds` from quote_lmsr_sell(full position) keyed by bet id */
  lmsrSellQuoteByBetId?: Record<string, number>;
}

interface BuildPortfolioOptions {
  includeWalletBalance?: boolean;
}

export interface PortfolioDataResult {
  positions: LocalPositionWithMarket[];
  transactions: DetailedWalletTransaction[];
  createdMarkets: Market[];
  walletBalance?: number;
  totalTrades: number;
  /** Count of settled (won or lost) trades — used for the Win Rate sub-label. */
  settledTrades: number;
  winRate: number;
  lmsrMarkets?: Record<string, LMSRMarketState>;
}

const asMarketCategory = (value: string | null): MarketCategory => {
  if (!value) return 'Other';
  const category = value as MarketCategory;
  return category;
};

const asMarketStatus = (value: string): MarketStatus => {
  return (value as MarketStatus) || 'active';
};

const asMarketType = (value: string | null): DbMarketType => {
  if (value === 'knockout') return 'knockout';
  return 'fixed';
};

/** Matches main portfolio fetch so wallet-linked bets resolve market/outcome enrichment. */
const USER_BETS_PORTFOLIO_SELECT = `
  *,
  market:markets!inner(
    id,
    title,
    category,
    resolution_time,
    trading_end_time,
    status,
    market_type,
    engine_type,
    lmsr_b
  ),
  market_option:market_options!inner(
    id,
    name,
    trading_end_time
  )
`;

/**
 * Loads any user_bets rows referenced by wallet bet_placement that are missing from `bets`
 * (e.g. last 50 bets skipped an older trade still cited on the ledger).
 */
async function mergeBetsReferencedByWalletPlacements(
  userId: string,
  bets: PortfolioBet[],
  transactions: WalletTransaction[],
): Promise<PortfolioBet[]> {
  const byId = new Map(bets.map((b) => [b.id, b]));
  const referenced = new Set<string>();
  for (const tx of transactions) {
    if (tx.type !== 'bet_placement' || !tx.related_entity_id) continue;
    referenced.add(String(tx.related_entity_id));
  }
  const missing = [...referenced].filter((id) => !byId.has(id));
  if (missing.length === 0) {
    return bets;
  }

  const chunkSize = 40;
  for (let i = 0; i < missing.length; i += chunkSize) {
    const chunk = missing.slice(i, i + chunkSize);
    const { data: extraRows, error } = await supabase
      .from('user_bets')
      .select(USER_BETS_PORTFOLIO_SELECT)
      .eq('user_id', userId)
      .in('id', chunk);

    if (error) {
      logger.warn('mergeBetsReferencedByWalletPlacements: extra bet fetch failed', {
        message: error.message,
      });
      continue;
    }
    for (const row of (extraRows || []) as PortfolioBet[]) {
      byId.set(row.id, row);
    }
  }

  return Array.from(byId.values()).sort((a, b) => {
    const ta = new Date(a.time).getTime();
    const tb = new Date(b.time).getTime();
    return tb - ta;
  });
}

export async function fetchUserPortfolioData(
  userId: string,
  options: FetchPortfolioDataOptions = {}
): Promise<PortfolioDataResult> {
  try {
    const betLimit = options.betLimit ?? 50;

    const { data: betData, error: betError } = await supabase
      .from('user_bets')
      .select(USER_BETS_PORTFOLIO_SELECT)
      .eq('user_id', userId)
      .neq('status', 'cancelled')
      .order('time', { ascending: false })
      .limit(betLimit);

    if (betError) {
      logger.error('Error fetching portfolio bets:', betError);
      throw betError;
    }

    let bets = (betData || []) as unknown as PortfolioBet[];

    const { data: transactionData, error: transactionError } = await supabase
      .from('wallet_transactions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (transactionError) {
      logger.error('Error fetching wallet transactions:', transactionError);
      throw transactionError;
    }

    const transactions = (transactionData || []) as WalletTransaction[];

    bets = await mergeBetsReferencedByWalletPlacements(userId, bets, transactions);

    const lmsrMarketIds = [
      ...new Set(
        bets
          .filter((b) => (b.market?.engine_type ?? 'pool') === 'lmsr')
          .map((b) => b.market_id),
      ),
    ];
    const lmsrOptionsByMarket = new Map<string, { id: number; outstanding_shares: number }[]>();
    if (lmsrMarketIds.length > 0) {
      const { data: optRows, error: optErr } = await supabase
        .from('market_options')
        .select('id, market_id, outstanding_shares')
        .in('market_id', lmsrMarketIds)
        .eq('status', 'active')
        .order('id', { ascending: true });
      if (optErr) {
        logger.error('Error fetching LMSR option state for portfolio:', optErr);
      } else {
        for (const row of optRows || []) {
          const mid = row.market_id as string;
          const list = lmsrOptionsByMarket.get(mid) ?? [];
          list.push({
            id: row.id as number,
            outstanding_shares: Number(row.outstanding_shares ?? 0),
          });
          lmsrOptionsByMarket.set(mid, list);
        }
      }
    }

    const marketIds = bets.map((bet) => bet.market_id);
    const uniqueMarketIds = [...new Set(marketIds)];
    const marketPoolRows: MarketPoolRow[] = [];

    if (uniqueMarketIds.length > 0) {
      const chunkSize = 20;
      for (let i = 0; i < uniqueMarketIds.length; i += chunkSize) {
        const chunk = uniqueMarketIds.slice(i, i + chunkSize);
        const { data: poolData, error: poolError } = await supabase
          .from('market_pools')
          .select('market_id, market_option_id, total_amount')
          .in('market_id', chunk);

        if (poolError) {
          logger.error('Error fetching market pools chunk:', poolError);
          continue;
        }

        (poolData || []).forEach((pool) => {
          marketPoolRows.push({
            market_id: pool.market_id,
            market_option_id: pool.market_option_id,
            total_amount: pool.total_amount,
          });
        });
      }
    }

    const { data: createdData, error: createdError } = await supabase
      .from('markets')
      .select(`
        *,
        market_options!market_options_market_id_fkey (*),
        market_pools (*)
      `)
      .eq('created_by', userId)
      .order('created_at', { ascending: false });

    if (createdError) {
      logger.error('Error fetching created markets:', createdError);
      throw createdError;
    }
    let walletBalance: number | null | undefined = undefined;

    if (options.includeWalletBalance !== false) {
      const { data: walletData, error: walletError } = await supabase
        .from('user_wallets')
        .select('balance')
        .eq('user_id', userId)
        .maybeSingle();

      if (walletError) {
        logger.error('Error fetching wallet balance:', walletError);
      } else {
        walletBalance = walletData?.balance ?? 0;
      }
    }

    const lmsrSellQuoteByBetId: Record<string, number> = {};
    const betsForQuotes = bets.filter(
      (b) =>
        (b.market?.engine_type ?? 'pool') === 'lmsr' &&
        b.status === 'active' &&
        Number(b.shares_owned ?? 0) > 0,
    );
    await Promise.all(
      betsForQuotes.map(async (b) => {
        try {
          const { data, error } = await supabase.rpc('quote_lmsr_sell', {
            p_market_id: b.market_id,
            p_market_option_id: b.market_option_id,
            p_shares: Number(b.shares_owned),
          });
          if (error) {
            logger.warn('quote_lmsr_sell RPC error', { betId: b.id, error: error.message });
            return;
          }
          const row = data as { success?: boolean; final_proceeds?: unknown; error?: string };
          if (row && row.success !== false && typeof row.final_proceeds === 'number') {
            lmsrSellQuoteByBetId[b.id] = row.final_proceeds;
          }
        } catch (e) {
          logger.warn('quote_lmsr_sell failed', { betId: b.id, error: e });
        }
      }),
    );

    return buildPortfolioDataResult(
      {
        bets,
        marketPools: marketPoolRows,
        transactions,
        createdMarkets: (createdData || []) as RawCreatedMarket[],
        walletBalance,
        lmsrOptionsByMarket,
        lmsrSellQuoteByBetId,
      },
      { includeWalletBalance: options.includeWalletBalance !== false }
    );
  } catch (error) {
    logger.error('Failed to fetch user portfolio data:', error);
    throw error;
  }
}

export async function buildPortfolioDataResult(
  raw: RawPortfolioDataInput,
  options: BuildPortfolioOptions = {}
): Promise<PortfolioDataResult> {
  try {
    const includeWalletBalance = options.includeWalletBalance !== false;

    const marketPoolsData: Record<string, Record<number, number>> = {};
    for (const pool of raw.marketPools) {
      if (!marketPoolsData[pool.market_id]) {
        marketPoolsData[pool.market_id] = {};
      }
      marketPoolsData[pool.market_id][pool.market_option_id] = pool.total_amount;
    }

    const positions: PortfolioDataResult['positions'] = [];

    const placementDebitByBetId = new Map<string, number>();
    for (const tx of raw.transactions ?? []) {
      const t = tx as WalletTransaction;
      if (t.type === 'bet_placement' && t.related_entity_id) {
        placementDebitByBetId.set(String(t.related_entity_id), Math.abs(Number(t.amount)));
      }
    }

    raw.bets.forEach((bet) => {
      if (!bet.market || !bet.market_option) {
        logger.warn('Skipping bet due to missing relations', { betId: bet.id });
        return;
      }

      const engineType = bet.market.engine_type ?? 'pool';
      const sharesOwned = Number(bet.shares_owned ?? 0);

      const marketPools = marketPoolsData[bet.market_id] || {};
      const totalMarketPool = Object.values(marketPools).reduce((sum, amount) => sum + amount, 0);
      const optionPool = marketPools[bet.market_option_id] || 0;

      let currentProbability = 0;
      if (totalMarketPool > 0) {
        currentProbability = optionPool / totalMarketPool;
      }
      currentProbability = Math.max(0, Math.min(1, currentProbability));

      let currentOdds = currentProbability > 0 ? 1 / currentProbability : 1000;
      if (!Number.isFinite(currentOdds)) {
        currentOdds = 1000;
      }

      let initGross =
        typeof bet.lmsr_initial_stake_gross === 'number' ? bet.lmsr_initial_stake_gross : null;
      let initNet =
        typeof bet.lmsr_initial_stake_net === 'number' ? bet.lmsr_initial_stake_net : null;
      const wlDebit = placementDebitByBetId.get(bet.id);
      if (engineType === 'lmsr' && (initGross == null || initNet == null) && wlDebit != null) {
        if (initGross == null) initGross = wlDebit;
        if (initNet == null)
          initNet = Math.max(0, wlDebit - Number(bet.service_fee_amount ?? 0));
      }

      const amtAfterFeeBase = Number(bet.amount_after_fee ?? bet.amount ?? 0);
      const amtGrossBase = Number(bet.amount ?? 0);
      const spreadRevFull = Number(bet.spread_revenue ?? 0);

      let rowAmountAfterFee =
        engineType === 'lmsr' && sharesOwned === 0 && initNet != null ? initNet : amtAfterFeeBase;
      let rowAmount =
        engineType === 'lmsr' && sharesOwned === 0 && initGross != null ? initGross : amtGrossBase;
      let rowServiceFee =
        engineType === 'lmsr' && sharesOwned === 0 && initGross != null && initNet != null
          ? Math.max(0, Number(initGross) - Number(initNet))
          : Number(bet.service_fee_amount ?? 0);

      if (engineType === 'lmsr') {
        const grossForLine = rowAmount;
        const initialGrossForScale =
          typeof bet.lmsr_initial_stake_gross === 'number' && bet.lmsr_initial_stake_gross > 0
            ? bet.lmsr_initial_stake_gross
            : wlDebit != null && wlDebit > 0
              ? wlDebit
              : null;
        let spreadPortion = 0;
        if (spreadRevFull > 0 && grossForLine > 0) {
          const denom =
            initialGrossForScale != null && initialGrossForScale > 0
              ? initialGrossForScale
              : grossForLine;
          spreadPortion = spreadRevFull * (grossForLine / denom);
        }
        rowServiceFee = Number(bet.service_fee_amount ?? 0) + spreadPortion;
        rowAmountAfterFee = Math.max(0, grossForLine - rowServiceFee);
      } else {
        const implicitPoolFee = Math.max(0, amtGrossBase - amtAfterFeeBase);
        rowServiceFee = Number(bet.service_fee_amount ?? 0) || implicitPoolFee;
        rowAmount = amtGrossBase;
        rowAmountAfterFee = amtAfterFeeBase;
      }

      let currentPotentialWin = amtAfterFeeBase * currentOdds;
      let unrealizedPnl = amtAfterFeeBase * currentOdds - amtAfterFeeBase;
      let realizedPnl = 0;
      let current_market_value: number | undefined;
      let current_price_per_share: number | undefined;

      if (engineType === 'lmsr' && sharesOwned > 0) {
        currentPotentialWin = sharesOwned;
        const netStake = rowAmountAfterFee;
        currentOdds = netStake > 0 ? sharesOwned / netStake : 0;

        const opts = raw.lmsrOptionsByMarket?.get(bet.market_id);
        const mb = Number(bet.market?.lmsr_b ?? 0);
        const quotedFinal = raw.lmsrSellQuoteByBetId?.[bet.id];

        if (opts && opts.length > 0 && mb > 0) {
          const idx = opts.findIndex((o) => o.id === bet.market_option_id);
          const sharesVec = opts.map((o) => Number(o.outstanding_shares ?? 0));
          if (idx >= 0) {
            const pxMarginal = lmsrPrice(mb, sharesVec, idx);
            if (quotedFinal != null && Number.isFinite(quotedFinal)) {
              current_market_value = quotedFinal;
              current_price_per_share = sharesOwned > 0 ? quotedFinal / sharesOwned : pxMarginal;
              unrealizedPnl = quotedFinal - netStake;
              currentProbability = pxMarginal;
            } else {
              logger.warn('Using marginal LMSR mark (quote_lmsr_sell unavailable)', {
                betId: bet.id,
                marketId: bet.market_id,
              });
              const grossInt = lmsrGrossSellProceeds(mb, sharesVec, idx, sharesOwned);
              current_price_per_share = pxMarginal;
              current_market_value = grossInt;
              unrealizedPnl = grossInt - netStake;
              currentProbability = pxMarginal;
            }
          } else {
            unrealizedPnl = sharesOwned - netStake;
            currentProbability = 0;
          }
        } else {
          unrealizedPnl = sharesOwned - netStake;
          currentProbability = 0;
        }
      } else if (engineType === 'lmsr') {
        currentPotentialWin = 0;
        currentOdds = 0;
        unrealizedPnl = 0;
        const opts = raw.lmsrOptionsByMarket?.get(bet.market_id);
        const mb = Number(bet.market?.lmsr_b ?? 0);
        if (opts && opts.length > 0 && mb > 0) {
          const idx = opts.findIndex((o) => o.id === bet.market_option_id);
          if (idx >= 0) {
            const sharesVec = opts.map((o) => Number(o.outstanding_shares ?? 0));
            currentProbability = lmsrPrice(mb, sharesVec, idx);
          }
        }

        const ex = typeof bet.lmsr_exit_net === 'number' ? bet.lmsr_exit_net : null;
        const iniNetForPl = rowAmountAfterFee;
        if (ex != null && typeof iniNetForPl === 'number') {
          realizedPnl = ex - iniNetForPl;
        }
      }

      positions.push({
        betId: bet.id,
        userId: bet.user_id,
        marketId: bet.market_id,
        marketTitle: bet.market.title,
        marketCategory: asMarketCategory(bet.market.category),
        marketStatus: asMarketStatus(bet.market.status),
        marketType: asMarketType(bet.market.market_type),
        marketTradingEndTime: bet.market.trading_end_time,
        marketResolutionTime: bet.market.resolution_time,
        market_option_id: bet.market_option_id,
        option_name: bet.market_option.name,
        optionTradingEndTime: bet.market_option.trading_end_time,
        amount: rowAmount,
        amountAfterFee: rowAmountAfterFee,
        serviceFeeAmount: rowServiceFee,
        probability: currentProbability,
        status: bet.status,
        initial_potential_win: bet.initial_potential_win ?? undefined,
        currentPotentialWin,
        unrealizedPnl,
        currentOdds,
        realizedPnl,
        betTime: bet.time,
        engine_type: engineType === 'lmsr' ? 'lmsr' : 'pool',
        shares_owned: bet.shares_owned ?? null,
        current_market_value,
        current_price_per_share,
        lmsr_initial_stake_gross:
          typeof initGross === 'number' ? initGross : bet.lmsr_initial_stake_gross ?? undefined,
        lmsr_initial_stake_net:
          typeof initNet === 'number' ? initNet : bet.lmsr_initial_stake_net ?? undefined,
        lmsr_exit_net:
          typeof bet.lmsr_exit_net === 'number' ? bet.lmsr_exit_net : undefined,
      });
    });

    const completedBets = raw.bets.filter((bet) => bet.status === 'won' || bet.status === 'lost');
    const winningBets = completedBets.filter((bet) => bet.status === 'won');
    const winRate = completedBets.length > 0 ? (winningBets.length / completedBets.length) * 100 : 0;

    const betMap = new Map(raw.bets.map((bet) => [bet.id, bet]));

    const detailedTransactions = (raw.transactions || []).map((tx) => {
      const baseTx = tx as WalletTransaction;
      const relatedBet = baseTx.related_entity_id ? betMap.get(baseTx.related_entity_id) : undefined;

      // Every bet_placement debit should resolve to market + outcome when we have the bet row
      // (do not require related_entity_type === 'bet'; some rows omit or differ).
      if (
        baseTx.type === 'bet_placement' &&
        relatedBet &&
        relatedBet.market &&
        relatedBet.market_option
      ) {
        return {
          ...baseTx,
          marketTitle: relatedBet.market.title,
          outcomeName: relatedBet.market_option.name,
          marketId: relatedBet.market_id,
        };
      }
      if (
        relatedBet &&
        (baseTx.related_entity_type === 'bet' || baseTx.related_entity_type === 'bet_payout') &&
        relatedBet.market &&
        relatedBet.market_option
      ) {
        return {
          ...baseTx,
          marketTitle: relatedBet.market.title,
          outcomeName: relatedBet.market_option.name,
          marketId: relatedBet.market_id,
        };
      }
      // LMSR sell: wallet row uses related_entity_type = market; parse outcome from description.
      if (
        baseTx.related_entity_type === 'market' &&
        baseTx.related_entity_id &&
        baseTx.type === 'payout' &&
        typeof baseTx.description === 'string' &&
        baseTx.description.includes('Sold shares on')
      ) {
        const outcomeGuess = baseTx.description.match(/Sold shares on "([^"]+)"/)?.[1];
        const sampleBet = raw.bets.find((b) => b.market_id === baseTx.related_entity_id);
        return {
          ...baseTx,
          marketId: baseTx.related_entity_id,
          marketTitle: sampleBet?.market?.title,
          outcomeName: outcomeGuess,
        };
      }
      return {
        ...baseTx,
      };
    }) as PortfolioDataResult['transactions'];

    const processedCreatedMarkets: Market[] = (await Promise.all(
      (raw.createdMarkets || []).map(async (market) => {
        try {
          const displayMarket = await marketEngine.toDisplayMarket(market);
          return displayMarket;
        } catch (error) {
          logger.error('Error processing created market:', { error, marketId: market?.id });
          return null;
        }
      })
    ))
      .filter((market): market is Market => market !== null)
      .map((market) => market);

    const lmsrMarkets: Record<string, LMSRMarketState> = {};
    raw.lmsrOptionsByMarket?.forEach((options, marketId) => {
      const sample = raw.bets.find(
        (b) => b.market_id === marketId && (b.market?.engine_type ?? 'pool') === 'lmsr',
      );
      const b = Number(sample?.market?.lmsr_b ?? 0);
      if (b > 0 && options.length) {
        lmsrMarkets[marketId] = { lmsr_b: b, options };
      }
    });

    const result: PortfolioDataResult = {
      positions,
      transactions: detailedTransactions,
      createdMarkets: processedCreatedMarkets,
      totalTrades: raw.bets.length,
      settledTrades: completedBets.length,
      winRate,
      lmsrMarkets: Object.keys(lmsrMarkets).length ? lmsrMarkets : undefined,
    };

    if (includeWalletBalance) {
      result.walletBalance = raw.walletBalance ?? 0;
    }

    return result;
  } catch (error) {
    logger.error('Failed to build portfolio data result:', error);
    throw error;
  }
}

